<template>
  <div class="dashboard-lazy-ref">
    <BaseLoadingState
      v-if="loading"
      class="dashboard-lazy-ref__loading"
    />
    <div
      v-else-if="failed"
      class="dashboard-lazy-ref__error"
    >
      <span>Não foi possível carregar este conteúdo.</span>
      <button
        type="button"
        class="dashboard-lazy-ref__retry"
        @click="fetchChildren({ initial: true })"
      >
        Tentar novamente
      </button>
    </div>
    <template v-else>
      <DashboardNodeRenderer
        v-for="(child, index) in transformedChildren"
        :key="child.id || index"
        :node="child"
        :data-field="childDataField(child, index)"
        @row-detail="$emit('row-detail', $event)"
        @input="$emit('input', $event)"
        @filter-change="onFilterChange"
        @update:options="onTableOptionsChange"
        @update:reference-date="$emit('update:reference-date', $event)"
        @submit="onFilterSubmit"
        @action="$emit('action', $event)"
      />
    </template>
    <BaseSnackbar
      :active-snackbar="feedback"
      :type-snackbar="feedbackColor"
      :text-snackbar="feedbackMessage"
      @closeSnackbar="feedback = !feedback"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import DashboardTableQueryState from '../../mixins/DashboardTableQueryState'
import { childDataField as resolveChildDataField } from './dataFieldUtils'
import BaseLoadingState from '../BaseLoadingState.vue'
import BaseSnackbar from '../BaseSnackbar'
import BaseSnackbarConfiguration from '../../mixins/BaseSnackbarConfiguration'

export default {
  name: 'DashboardLazyRef',
  components: {
    BaseLoadingState,
    BaseSnackbar,
    DashboardNodeRenderer: () => Promise.resolve(require('./DashboardNodeRenderer.vue').default)
  },
  mixins: [DashboardTableQueryState, BaseSnackbarConfiguration],
  props: {
    instance: {
      type: String,
      required: true
    },
    params: {
      type: Object,
      required: false,
      default: () => ({})
    },
    view: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      loading: true,
      failed: false,
      rawChildren: []
    }
  },
  computed: {
    ...mapState({
      programInitiator: state => state.platformProgram.programInitiator
    }),
    baseScreen() {
      return { children: this.rawChildren }
    },
    transformedChildren() {
      const transformNode = (node) => {
        if (!node || typeof node !== 'object') {
          return node
        }
        if (node.type === 'lazy_ref') {
          return node
        }
        const clone = { ...node }
        const tableProps = this.applyNodeQueryState(node, transformNode)
        if (tableProps) {
          clone.props = tableProps
        }
        if (Array.isArray(node.sections)) {
          clone.sections = node.sections.map(transformNode)
        }
        if (Array.isArray(node.children)) {
          clone.children = node.children.map(transformNode)
        }
        return clone
      }
      return this.rawChildren.map(transformNode)
    }
  },
  created() {
    this.requestCount = 0
    this.fetchChildren({ initial: true })
  },
  methods: {
    ...mapActions(['apiGetScreenChildren']),
    performFetch() {
      return this.fetchChildren()
    },
    async fetchChildren({ initial = false } = {}) {
      this.requestCount += 1
      const requestId = this.requestCount
      const tableQueryRequestId = this.startTableQueryRequest()
      if (initial) {
        this.loading = true
        this.failed = false
      }
      try {
        const mergedParams = { ...this.params, ...this.tableQueryParams }
        const result = await this.apiGetScreenChildren({
          view: this.view || (this.$route && this.$route.params ? this.$route.params.view : undefined),
          programId: this.programInitiator,
          instance: this.instance,
          params: mergedParams
        })
        if (requestId !== this.requestCount) {
          return
        }
        this.rawChildren = Array.isArray(result)
          ? result
          : (result && Array.isArray(result.children) ? result.children : [])
        this.confirmTableOptions(tableQueryRequestId)
      } catch (error) {
        if (requestId !== this.requestCount) {
          return
        }
        this.rollbackTableOptions(tableQueryRequestId)
        if (initial) {
          this.failed = true
        } else {
          this.showFeedback({ color: 'error', message: 'Não foi possível atualizar os dados. Tente novamente.' })
        }
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            `DashboardLazyRef: falha ao buscar conteúdo do instance "${this.instance}".`,
            error
          )
        }
      } finally {
        if (requestId === this.requestCount) {
          this.loading = false
        }
      }
    },
    childDataField(child, index) {
      return resolveChildDataField({ props: { instance: this.instance } }, child, index)
    }
  }
}
</script>

<style lang="scss">
.dashboard-lazy-ref {
  &__error {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 0.8rem;
    color: var(--ds-color-black-055);
  }

  &__retry {
    cursor: pointer;
    background: none;
    border: 1px solid var(--ds-color-black-010);
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v-textPrimary-base);

    &:hover {
      background: var(--ds-color-black-005);
    }
  }
}
</style>
